import { useState, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Separator } from '../ui/separator';
import { Eye, EyeOff, Loader2, Volume2, VolumeX, Mail } from 'lucide-react';
import { projectId } from '../../utils/supabase/info';
import { toast } from 'sonner';
import type { FocusModeSettings as FocusModeSettingsType } from './types';

interface FocusModeSettingsProps {
  userId: string;
  userRole: string;
  accessToken: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function FocusModeSettingsDialog({
  userId,
  userRole,
  accessToken,
  open,
  onOpenChange,
}: FocusModeSettingsProps) {
  const [settings, setSettings] = useState<FocusModeSettingsType>({
    userId,
    enabled: false,
    suppressNonUrgent: true,
    disableToastPopups: false,
    disableSound: false,
    emailFrequency: 'immediate',
    updatedAt: new Date().toISOString(),
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const baseUrl = `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd`;

  const isEL = ['Engagement Lead', 'engagement_lead', 'systemAdmin', 'system_admin'].includes(userRole);

  useEffect(() => {
    if (open) loadSettings();
  }, [open]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${baseUrl}/engagement-messaging/focus-mode?userId=${userId}`,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setSettings(data.settings);
      }
    } catch (error) {
      console.error('Error loading focus mode settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    try {
      setSaving(true);
      const response = await fetch(
        `${baseUrl}/engagement-messaging/focus-mode`,
        {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ ...settings, userId }),
        }
      );
      if (response.ok) {
        toast.success('Notification preferences saved');
        onOpenChange(false);
      } else {
        toast.error('Failed to save preferences');
      }
    } catch (error) {
      console.error('Error saving focus mode:', error);
      toast.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {settings.enabled ? (
              <EyeOff className="h-5 w-5 text-violet-600" />
            ) : (
              <Eye className="h-5 w-5" />
            )}
            Notification Preferences
          </DialogTitle>
          <DialogDescription>
            Manage how and when you receive engagement notifications.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-6 py-2">
            {/* Focus Mode */}
            <div>
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-medium">Focus Mode</Label>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Suppress non-urgent notifications
                  </p>
                </div>
                <Switch
                  checked={settings.enabled}
                  onCheckedChange={(checked) =>
                    setSettings((prev) => ({ ...prev, enabled: checked }))
                  }
                  disabled={isEL}
                />
              </div>
              {isEL && (
                <p className="text-[10px] text-amber-600 mt-1">
                  Firm Engagement Leads always receive all notifications.
                </p>
              )}
              {settings.enabled && (
                <div className="mt-3 ml-1 pl-3 border-l-2 border-violet-200">
                  <p className="text-xs text-muted-foreground">
                    While Focus Mode is active, only Urgent messages will trigger notifications.
                    Informational and Action Required messages will be available when you return.
                  </p>
                </div>
              )}
            </div>

            <Separator />

            {/* Toast Popups */}
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm font-medium">Toast Popups</Label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Show in-app popup notifications
                </p>
              </div>
              <Switch
                checked={!settings.disableToastPopups}
                onCheckedChange={(checked) =>
                  setSettings((prev) => ({ ...prev, disableToastPopups: !checked }))
                }
              />
            </div>

            {/* Sound */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {settings.disableSound ? (
                  <VolumeX className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <Volume2 className="h-4 w-4 text-muted-foreground" />
                )}
                <div>
                  <Label className="text-sm font-medium">Sound</Label>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Play notification sounds
                  </p>
                </div>
              </div>
              <Switch
                checked={!settings.disableSound}
                onCheckedChange={(checked) =>
                  setSettings((prev) => ({ ...prev, disableSound: !checked }))
                }
              />
            </div>

            <Separator />

            {/* Email Frequency */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <Label className="text-sm font-medium">Email Frequency</Label>
              </div>
              <Select
                value={settings.emailFrequency}
                onValueChange={(v) =>
                  setSettings((prev) => ({
                    ...prev,
                    emailFrequency: v as FocusModeSettingsType['emailFrequency'],
                  }))
                }
              >
                <SelectTrigger className="text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="immediate">Immediate</SelectItem>
                  <SelectItem value="hourly">Hourly Digest</SelectItem>
                  <SelectItem value="daily">Daily Digest</SelectItem>
                  <SelectItem value="weekly">Weekly Digest</SelectItem>
                  <SelectItem value="off">Off</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Save */}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button onClick={saveSettings} disabled={saving}>
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                ) : null}
                Save Preferences
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}